import { useState, useRef, useCallback } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';

/**
 * Custom hook for managing SSE streams for various operations
 * (custom fields, email drafts, chat, etc.)
 * 
 * Supported endpoints:
 * - /campaigns/{id}/custom-fields/preview/stream - Preview custom field generation
 * - /campaigns/{id}/configs/{config_id}/approve/stream - Approve custom field generation (custom_fields_api)
 * - /campaigns/{id}/draft-emails/preview/stream - Preview email drafts
 * - /campaigns/{id}/configs/{config_id}/approve/stream - Approve email drafts (emails_api)
 * 
 * @param {Object} config - Configuration object
 * @param {Function} config.onStatus - Callback for status updates (progress, completion percentage)
 * @param {Function} config.onData - Callback for data events (field definitions, updates)
 * @param {Function} config.onComplete - Callback when stream completes
 * @param {Function} config.onError - Callback for errors
 * @returns {Object} - Stream control methods and state
 * 
 * @example
 * // Custom field approval
 * const { startStream, stopStream, isStreaming } = useSSEStream({
 *   onStatus: (status) => console.log('Progress:', status.completion_percentage),
 *   onData: (data) => console.log('Field update:', data),
 *   onComplete: () => console.log('Generation complete'),
 *   onError: (error) => console.error('Error:', error)
 * });
 * 
 * await startStream(`/campaigns/${campaignId}/configs/${configId}/approve/stream`);
 * 
 * @example
 * // Email draft preview
 * await startStream(`/campaigns/${campaignId}/draft-emails/preview/stream`, {
 *   method: 'POST',
 *   body: { version_id: versionId, drafting_params: {...} }
 * });
 */
const useSSEStream = ({ onStatus, onData, onComplete, onError }) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const abortControllerRef = useRef(null);
  const streamActiveRef = useRef(false);

  /**
   * Start an SSE stream
   * @param {string} url - The SSE endpoint URL
   * @param {Object} options - Additional options (method, body, headers)
   */
  const startStream = useCallback(async (url, options = {}) => {
    // Prevent multiple simultaneous streams
    if (streamActiveRef.current) {
      console.warn('Stream already active, ignoring new stream request');
      return;
    }

    const token = localStorage.getItem('token');
    const anonymousUserId = localStorage.getItem('anonymous_user_id');
    
    // Require either token or anonymous user ID
    if (!token && !anonymousUserId) {
      onError?.({ message: 'Authentication required' });
      return;
    }

    // Create abort controller for this stream
    abortControllerRef.current = new AbortController();
    streamActiveRef.current = true;
    setIsStreaming(true);

    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const fullUrl = url.startsWith('http') ? url : `${apiBase}${url}`;

    // Build headers - include either Authorization or X-Anonymous-User-Id
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      ...options.headers,
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else if (anonymousUserId) {
      headers['X-Anonymous-User-Id'] = anonymousUserId;
    }

    try {
      await fetchEventSource(fullUrl, {
        method: options.method || 'POST',
        headers,
        body: options.body ? JSON.stringify(options.body) : undefined,
        signal: abortControllerRef.current.signal,

        async onopen(response) {
          if (response.ok) {
            return;
          }
          
          // Handle error responses
          if (response.status === 401) {
            throw new Error('Authentication failed');
          } else if (response.status === 402) {
            // Insufficient credits - preserve structured FastAPI detail for InsufficientCreditsContext
            const text = await response.text();
            let detail = 'Insufficient credits for this operation.';
            try {
              const errorData = JSON.parse(text);
              detail = errorData.detail ?? detail;
            } catch (e) {
              detail = text || detail;
            }
            const message =
              typeof detail === 'string'
                ? detail
                : detail?.message || 'Insufficient credits for this operation.';
            const error = new Error(message);
            error.status = 402;
            error.detail = detail;
            throw error;
          } else if (response.status >= 400) {
            const text = await response.text();
            throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
          }
        },

        onmessage(event) {
          if (!event.data) return;

          try {
            const data = JSON.parse(event.data);

            // Handle different event types
            switch (data.event) {
              case 'start':
                // Forward to onData so callers can capture config_id or other start payload.
                onData?.(data);
                break;

              case 'status':
                onStatus?.(data.data);
                break;

              case 'end':
                streamActiveRef.current = false;
                setIsStreaming(false);
                onComplete?.();
                break;

              case 'error':
                console.error('Stream error:', data.data);
                onError?.(data.data);
                streamActiveRef.current = false;
                setIsStreaming(false);
                break;

              case 'custom_field_definition':
              case 'companies_custom_field_update':
              case 'contacts_custom_field_update':
              case 'sec_filings_custom_field_update':
              case 'companies':
              case 'contacts':
              case 'contact_field_definition':
              case 'email_draft_update':
              case 'email_batch_complete':
                // Pass data events to the data handler
                onData?.(data);
                break;

              default:
                // Pass unknown events to data handler
                onData?.(data);
                break;
            }
          } catch (e) {
            console.error('Error parsing SSE data:', e);
          }
        },

        onerror(error) {
          console.error('SSE error:', error);
          streamActiveRef.current = false;
          setIsStreaming(false);
          
          // Only call onError if stream was actually active (not a normal close)
          if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
            onError?.({ message: error.message || 'Stream error occurred' });
          }
          
          // Don't retry - throw to stop reconnection attempts
          throw error;
        },

        openWhenHidden: true,
      });
    } catch (error) {
      console.error('Failed to start SSE stream:', error);
      streamActiveRef.current = false;
      setIsStreaming(false);
      
      if (error.name !== 'AbortError') {
        onError?.({
          message: error.message || 'Failed to start stream',
          status: error.status,
          detail: error.detail,
        });
      }
    }
  }, [onStatus, onData, onComplete, onError]);

  /**
   * Stop the current stream
   */
  const stopStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    streamActiveRef.current = false;
    setIsStreaming(false);
  }, []);

  /**
   * Cleanup function - should be called on component unmount
   */
  const cleanup = useCallback(() => {
    stopStream();
  }, [stopStream]);

  return {
    isStreaming,
    startStream,
    stopStream,
    cleanup,
  };
};

export default useSSEStream;

